import { useContext, useMemo, useState } from 'react';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import defaultHero from '@r/assets/images/fiddy/default-image.png';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@ui/card.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ui/table.tsx';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  CalendarDaysIcon,
  ChevronDown,
  CoinsIcon,
  DownloadIcon,
  RocketIcon,
  ShoppingCartIcon,
  ArrowDownIcon,
  ArrowDownUpIcon,
  ArrowUpIcon,
  TrophyIcon,
  Gift
} from 'lucide-react';
import { Button } from '@ui/button.tsx';
import { WorkspaceStepsInfoProcessor } from '@r/utils/workspaceSteps/workspace-steps-info-processor.ts';
import { Trans, useTranslation } from 'react-i18next';
import { Skeleton } from '@ui/skeleton.tsx';
import pointsTranslation from '@r/translations/points-translation.ts';
import Marquee from '@ui/marquee.tsx';
import { LinkProtected } from '@r/components/link-protected/link-protected.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { useUser } from '@r/api/auth.tsx';
import { useBalance } from '@r/context/balance-context.ts';
import { isStepLogicWorkspace } from '@r/utils/workspace-utils.ts';
import type {
  ApiReferencesGetCollectionParams,
  BoosterBoosterReadModel,
  LogicTypeStepWorkspaceReadModel,
  ReferenceReferenceReadModel
} from '@r/api/generated/client.schemas.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useProductReferenceEnabled } from '@r/hooks/use-product-reference-enabled.ts';
import { useAllReferences } from '@r/hooks/use-all-references.ts';
import { PageContainer } from '@client/components/layout/page-container.tsx';
import { useActiveBoosters } from '../../hooks/use-active-boosters.ts';
import {
  getBoosterMultiplierSummary,
  getBoosterProgramTableRows
} from './program-booster-display.ts';

type WorkspaceStep = LogicTypeStepWorkspaceReadModel;
type ProductReference = ReferenceReferenceReadModel;
type ProductReferenceSortKey = 'brand' | 'productLine' | 'name' | 'uniqueCode' | 'pointValue';
type ProductReferenceSortDirection = 'asc' | 'desc';

const formatBoosterDate = (value: string) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return format(date, 'dd/MM/yyyy', { locale: fr });
};

const BoosterSection = ({
  boosters,
  hidePoints
}: {
  boosters: BoosterBoosterReadModel[];
  hidePoints: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <section
      id="section-program-booster"
      className="flex flex-wrap items-start py-7 mb-20 w-full lg:mb-32"
    >
      <div className="mb-8 w-full lg:mb-0 lg:w-4/12">
        <h2 className="mb-6 text-center font-bold text-2xl text-foreground md:text-3xl lg:text-start lg:text-[2.75rem] lg:leading-[3.5rem]">
          {t('program.booster.title')}
        </h2>
        <p className="max-w-md text-center text-muted-foreground lg:text-start">
          {t('program.booster.description')}
        </p>
      </div>

      <div className="flex flex-col space-y-7 w-full lg:w-8/12 lg:pl-7">
        {boosters.map((booster) => {
          const tableRows = getBoosterProgramTableRows(booster);
          const multiplierSummary = getBoosterMultiplierSummary(booster);

          return (
            <Card key={booster.id} className="overflow-hidden rounded-3xl border-0">
              <div className="flex flex-col gap-5 p-7">
                <div className="flex gap-4 justify-start items-center">
                  <div className="flex justify-center items-center rounded-full bg-primary shrink-0 size-12">
                    <RocketIcon className="size-6 stroke-primary-foreground" aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-bold leading-tight text-foreground">
                    {t('program.booster.active.message')} {booster.title}
                  </h3>
                </div>

                {booster.description && (
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {booster.description}
                  </p>
                )}

                <div className="flex flex-col gap-y-2 gap-x-6 text-sm">
                  <span className="flex gap-2 items-center text-muted-foreground">
                    <CalendarDaysIcon className="size-4 text-muted-foreground" aria-hidden="true" />
                    {t('program.booster.period', {
                      startDate: formatBoosterDate(booster.startDate),
                      endDate: formatBoosterDate(booster.endDate)
                    })}
                  </span>
                  <span className="flex gap-2 items-center text-muted-foreground">
                    <CoinsIcon className="size-4 text-muted-foreground" aria-hidden="true" />
                    {multiplierSummary.type === 'single'
                      ? t('program.booster.multiplier', {
                          coefficient: multiplierSummary.coefficient
                        })
                      : t('program.booster.multiplier.variable')}
                  </span>
                </div>

                <p className="pt-1 font-semibold text-foreground">
                  {t('program.booster.references.label')}
                </p>
              </div>

              <Table className="min-w-[720px]">
                <TableHeader className="bg-primary">
                  <TableRow className="border-0 hover:bg-primary">
                    <TableHead className="h-14 font-semibold whitespace-nowrap text-primary-foreground">
                      {t('program.product.references.table.brand')}
                    </TableHead>
                    <TableHead className="h-14 font-semibold whitespace-nowrap text-primary-foreground">
                      {t('program.product.references.table.product.line')}
                    </TableHead>
                    <TableHead className="h-14 font-semibold whitespace-nowrap text-primary-foreground">
                      {t('program.product.references.table.product.name')}
                    </TableHead>
                    <TableHead className="h-14 font-semibold whitespace-nowrap text-primary-foreground">
                      {t('program.product.references.table.unique.code')}
                    </TableHead>
                    {!hidePoints && (
                      <TableHead className="h-14 font-semibold text-right whitespace-nowrap text-primary-foreground">
                        {t('program.product.references.table.points')}
                      </TableHead>
                    )}
                    <TableHead className="h-14 font-semibold text-right whitespace-nowrap text-primary-foreground">
                      {t('program.booster.references.table.min.quantity')}
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {tableRows.map((row, index) => (
                    <TableRow
                      key={row.rowKey}
                      className={
                        index % 2 === 0
                          ? 'bg-background hover:bg-primary/5'
                          : 'bg-primary/5 hover:bg-primary/10'
                      }
                    >
                      <TableCell className="font-medium text-foreground">{row.brand}</TableCell>
                      <TableCell className="text-foreground">
                        {row.productLine || <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell className="text-foreground">{row.name}</TableCell>
                      <TableCell className="whitespace-nowrap text-foreground">
                        {row.uniqueCode}
                      </TableCell>
                      {!hidePoints && (
                        <TableCell className="text-right whitespace-nowrap">
                          <div className="flex gap-2 justify-end items-center">
                            <span className="line-through text-muted-foreground">
                              {pointsTranslation(t, row.pointValue, true)}
                            </span>
                            <span className="font-semibold text-foreground">
                              {pointsTranslation(t, row.boostedPointValue, true)}
                            </span>
                            <CoinsIcon
                              className="size-4 shrink-0 text-primary"
                              aria-hidden="true"
                            />
                          </div>
                        </TableCell>
                      )}
                      <TableCell className="text-right font-semibold whitespace-nowrap text-foreground">
                        {row.minQuantity}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          );
        })}
      </div>
    </section>
  );
};

function ProductReferenceSortIcon({
  columnKey,
  sortKey,
  sortDirection
}: {
  columnKey: ProductReferenceSortKey;
  sortKey: ProductReferenceSortKey;
  sortDirection: ProductReferenceSortDirection;
}) {
  if (sortKey !== columnKey) {
    return <ArrowDownUpIcon className="ml-2 opacity-80 size-4" aria-hidden="true" />;
  }

  if (sortDirection === 'asc') {
    return <ArrowUpIcon className="ml-2 size-4" aria-hidden="true" />;
  }

  return <ArrowDownIcon className="ml-2 size-4" aria-hidden="true" />;
}

function ProductReferenceSortableHeader({
  columnKey,
  label,
  align = 'left',
  sortKey,
  sortDirection,
  onSort
}: {
  columnKey: ProductReferenceSortKey;
  label: string;
  align?: 'left' | 'right';
  sortKey: ProductReferenceSortKey;
  sortDirection: ProductReferenceSortDirection;
  onSort: (key: ProductReferenceSortKey) => void;
}) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={() => onSort(columnKey)}
      className={`flex w-full items-center ${align === 'right' ? 'justify-end text-right' : 'justify-start text-left'}`}
      aria-label={t('program.product.references.table.sort', {
        label,
        direction:
          sortKey === columnKey && sortDirection === 'desc'
            ? t('program.product.references.table.sort.desc')
            : t('program.product.references.table.sort.asc')
      })}
    >
      <span>{label}</span>
      <ProductReferenceSortIcon
        columnKey={columnKey}
        sortKey={sortKey}
        sortDirection={sortDirection}
      />
    </button>
  );
}

function ProgramLogicStepLevelIndicator() {
  const { t } = useTranslation();

  return (
    <div
      className="absolute top-[-4rem] flex animate-bounce items-center rounded-md bg-primary px-3 py-2 text-sm"
      style={{ boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.2)' }}
    >
      <div className="flex justify-center items-center mr-2 w-6 rounded-full border aspect-square border-foreground">
        <RocketIcon size={13} />
      </div>
      {t('program.logic.step.cursor.label')}
      <div className="absolute bottom-[-1.2rem] left-0 flex w-full justify-center">
        <ChevronDown className="stroke-primary" size={20} />
      </div>
    </div>
  );
}

function getWorkspaceStepKey(step: WorkspaceStep, index: number) {
  if (step.id != null) {
    return String(step.id);
  }

  return `${step.name}-${step.stepPoint}-${index}`;
}

const ProductReferenceSection = ({
  references,
  sortKey,
  sortDirection,
  onSort,
  hidePoints
}: {
  references: ProductReference[];
  sortKey: ProductReferenceSortKey;
  sortDirection: ProductReferenceSortDirection;
  onSort: (key: ProductReferenceSortKey) => void;
  hidePoints: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <section
      id="section-program-product-references"
      className="flex flex-wrap items-start py-7 mb-20 w-full lg:mb-32"
    >
      <div className="mb-8 w-full lg:mb-0 lg:w-4/12">
        <h2 className="mb-6 text-center font-bold text-2xl text-foreground md:text-3xl lg:text-start lg:text-[2.75rem] lg:leading-[3.5rem]">
          {t('program.product.references.title')}
        </h2>
        <p className="max-w-md text-center text-muted-foreground lg:text-start">
          {t('program.product.references.description')}
        </p>
      </div>

      <div className="w-full lg:w-8/12 lg:pl-7">
        <Card className="overflow-hidden rounded-3xl border-0">
          <Table className="min-w-[720px]">
            <TableHeader className="bg-primary">
              <TableRow className="border-0 hover:bg-primary">
                <TableHead className="h-14 font-semibold whitespace-nowrap text-primary-foreground">
                  <ProductReferenceSortableHeader
                    columnKey="brand"
                    label={t('program.product.references.table.brand')}
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                </TableHead>
                <TableHead className="h-14 font-semibold whitespace-nowrap text-primary-foreground">
                  <ProductReferenceSortableHeader
                    columnKey="productLine"
                    label={t('program.product.references.table.product.line')}
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                </TableHead>
                <TableHead className="h-14 font-semibold whitespace-nowrap text-primary-foreground">
                  <ProductReferenceSortableHeader
                    columnKey="name"
                    label={t('program.product.references.table.product.name')}
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                </TableHead>
                <TableHead className="h-14 font-semibold whitespace-nowrap text-primary-foreground">
                  <ProductReferenceSortableHeader
                    columnKey="uniqueCode"
                    label={t('program.product.references.table.unique.code')}
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                </TableHead>
                {!hidePoints && (
                  <TableHead className="h-14 font-semibold text-right whitespace-nowrap text-primary-foreground">
                    <ProductReferenceSortableHeader
                      columnKey="pointValue"
                      align="right"
                      label={t('program.product.references.table.points')}
                      sortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={onSort}
                    />
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>

            <TableBody>
              {references.map((reference, index) => (
                <TableRow
                  key={reference.id}
                  className={
                    index % 2 === 0
                      ? 'bg-background hover:bg-primary/5'
                      : 'bg-primary/5 hover:bg-primary/10'
                  }
                >
                  <TableCell className="font-medium text-foreground">{reference.brand}</TableCell>
                  <TableCell className="text-foreground">
                    {reference.productLine || <span className="text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell className="text-foreground">{reference.name}</TableCell>
                  <TableCell className="whitespace-nowrap text-foreground">
                    {reference.uniqueCode}
                  </TableCell>
                  {!hidePoints && (
                    <TableCell className="flex gap-2 justify-end items-center font-semibold text-right whitespace-nowrap text-foreground">
                      {pointsTranslation(t, reference.pointValue, true)}
                      <CoinsIcon className="size-4 shrink-0 text-primary" aria-hidden="true" />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </section>
  );
};

export const ProgramPage = () => {
  const { t } = useTranslation();

  const workspaceConfigContext = useContext(WorkspaceConfigContext);
  const isReferenceEnabled = useProductReferenceEnabled();
  const isReferenceHidden =
    workspaceConfigContext?.groupedConfigs?.core?.['hide-reference'] === 'true';
  const isReferencePointsHidden =
    workspaceConfigContext?.groupedConfigs?.core?.['hide-reference-points'] === 'true';
  const user = useUser().data;
  const [productReferenceSortKey, setProductReferenceSortKey] =
    useState<ProductReferenceSortKey>('brand');
  const [productReferenceSortDirection, setProductReferenceSortDirection] =
    useState<ProductReferenceSortDirection>('asc');

  const { workspace, isWorkspacePending, isWorkspaceError, isWorkspaceSuccess } =
    UseWorkspace(user);
  const { balance } = useBalance();
  const referenceQueryParams = useMemo<ApiReferencesGetCollectionParams>(
    () =>
      ({
        [`order[${productReferenceSortKey}]`]: productReferenceSortDirection
      }) as ApiReferencesGetCollectionParams,
    [productReferenceSortDirection, productReferenceSortKey]
  );
  const referencesQuery = useAllReferences(referenceQueryParams, {
    enabled: isReferenceEnabled && !isReferenceHidden
  });
  useOrvalQueryErrorToast(referencesQuery, t('toast.error.label'));
  const productReferences = referencesQuery.data ?? [];
  const shouldShowProductReferences =
    isReferenceEnabled && !isReferenceHidden && productReferences.length > 0;

  const { activeBoosters, hasActiveBooster } = useActiveBoosters();

  const handleProductReferenceSort = (key: ProductReferenceSortKey) => {
    if (productReferenceSortKey === key) {
      setProductReferenceSortDirection((currentDirection) =>
        currentDirection === 'asc' ? 'desc' : 'asc'
      );
      return;
    }

    setProductReferenceSortKey(key);
    setProductReferenceSortDirection('asc');
  };

  let workspaceSteps: WorkspaceStep[] = [];
  let lastReachedStep = null;

  if (isWorkspaceSuccess && workspace && user) {
    if (isStepLogicWorkspace(workspace)) {
      const { workspaceSteps: getWorkspaceSteps, lastReachedStep: getLastReachedStep } =
        WorkspaceStepsInfoProcessor(workspace, user);

      workspaceSteps = getWorkspaceSteps as WorkspaceStep[];
      lastReachedStep = getLastReachedStep;
    }
  }

  return (
    <PageContainer>
      <section id="section-program-hero" className="flex h-[42rem] w-full pt-10 pb-7">
        <div className="flex overflow-hidden relative justify-start items-end p-7 w-full rounded-3xl grow">
          <img
            src={workspaceConfigContext?.groupedConfigs?.media?.['program-hero'] || defaultHero}
            alt=""
            className="object-cover absolute inset-0 w-full h-full"
            onError={(e) => {
              e.currentTarget.src = defaultHero;
              e.currentTarget.onerror = null;
            }}
          />
          <Card className="flex relative z-10 flex-col justify-center w-full text-center rounded-2xl border-0 lg:w-4/12">
            <CardHeader>
              <CardTitle className="leading-normal text-start text-foreground">
                {t('program.overview.title', {
                  workspaceName: workspaceConfigContext?.groupedConfigs?.core?.title
                })}
              </CardTitle>
            </CardHeader>

            <CardContent className="text-start text-foreground">
              <Trans t={t} i18nKey="program.overview.content" components={{ 1: <br /> }} />
            </CardContent>

            <CardFooter className="text-foreground">
              <LinkProtected to="/terms-and-conditions" target={'_blank'}>
                <Button rounded="full">
                  <DownloadIcon className="mr-2" />
                  {t('program.overview.download.button')}
                </Button>
              </LinkProtected>
            </CardFooter>
          </Card>
        </div>
      </section>
      <Marquee text={workspaceConfigContext?.groupedConfigs?.core?.title ?? null} />

      <section
        id="section-program-description"
        className="mb-20 flex min-h-[80vh] w-full flex-wrap py-7 lg:mb-32"
      >
        <div className="w-full lg:w-4/12">
          <h1 className="mb-10 text-center font-bold text-2xl text-foreground md:text-3xl lg:mb-16 lg:text-start lg:text-[2.75rem] lg:leading-[3.5rem]">
            {t('program.explanation.title')}
          </h1>
        </div>
        <div className="flex flex-col space-y-7 w-full text-foreground lg:w-8/12 lg:pl-7">
          <Card className="flex flex-col justify-start items-center w-full text-center border-0 lg:flex-row">
            <div className="flex justify-center items-center p-10 pb-0 w-fit lg:p-16">
              <div className="flex justify-center items-center p-5 rounded-full bg-primary lg:p-10">
                <ShoppingCartIcon size={45} className="stroke-primary-foreground" />
              </div>
            </div>

            <div className="flex items-center lg:self-stretch">
              <div className="mr-10 hidden h-[70%] shrink-0 border-l-2 border-primary lg:block" />
              <div className="flex flex-col justify-center items-center p-7 space-y-4 lg:items-start">
                <div className="flex items-center">
                  <span className="text-xl font-medium text-start">
                    {t('program.explanation.step1.title')}
                  </span>
                </div>
                <div className="text-start">{t('program.explanation.step1.text1')}</div>
                <div className="text-start">{t('program.explanation.step1.text2')}</div>
                <div className="text-start">{t('program.explanation.step1.text3')}</div>
                <div className="text-sm text-start text-muted-foreground">
                  {t('program.explanation.step1.text4')}
                </div>
              </div>
            </div>
          </Card>

          <Card className="flex flex-col justify-start items-center w-full text-center border-0 lg:flex-row">
            <div className="flex justify-center items-center p-10 pb-0 w-fit lg:p-16">
              <div className="flex justify-center items-center p-5 rounded-full bg-primary lg:p-10">
                <CoinsIcon size={45} className="stroke-primary-foreground" />
              </div>
            </div>

            <div className="flex items-center lg:self-stretch">
              <div className="mr-10 hidden h-[70%] shrink-0 border-l-2 border-primary lg:block" />
              <div className="flex flex-col justify-center items-center p-7 space-y-4 lg:items-start">
                <div className="flex items-center">
                  <span className="text-xl font-medium text-start">
                    {t('program.explanation.step2.title')}
                  </span>
                </div>
                <div className="text-start">{t('program.explanation.step2.text1')}</div>
              </div>
            </div>
          </Card>

          <Card className="flex flex-col justify-start items-center w-full text-center border-0 lg:flex-row">
            <div className="flex justify-center items-center p-10 pb-0 w-fit lg:p-16">
              <div className="flex justify-center items-center p-5 rounded-full bg-primary lg:p-10">
                <Gift size={45} className="stroke-primary-foreground" />
              </div>
            </div>

            <div className="flex items-center lg:self-stretch">
              <div className="mr-10 hidden h-[70%] shrink-0 border-l-2 border-primary lg:block" />
              <div className="flex flex-col justify-center items-center p-7 space-y-4 lg:items-start">
                <div className="flex items-center">
                  <span className="text-xl font-medium text-start">
                    {t('program.explanation.step3.title')}
                  </span>
                </div>
                <div className="text-start">{t('program.explanation.step3.text1')}</div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {hasActiveBooster && activeBoosters.length > 0 && (
        <BoosterSection boosters={activeBoosters} hidePoints={isReferencePointsHidden} />
      )}

      {shouldShowProductReferences && (
        <ProductReferenceSection
          references={productReferences}
          sortKey={productReferenceSortKey}
          sortDirection={productReferenceSortDirection}
          onSort={handleProductReferenceSort}
          hidePoints={isReferencePointsHidden}
        />
      )}

      {isStepLogicWorkspace(workspace) && workspaceSteps && (
        <section id="section-program-steps" className="py-7">
          <div className="m-auto mb-20 w-10/12 lg:mb-32 lg:w-6/12">
            <h1 className="mb-10 text-center font-bold text-2xl text-foreground md:text-3xl lg:mb-16 lg:text-[2.75rem] lg:leading-[3.5rem]">
              {t('program.logic.step.title')}
            </h1>

            <p className="text-center">{t('program.logic.step.description')}</p>
          </div>

          <div className="flex flex-wrap justify-center m-auto mb-24 w-fit">
            <Card
              className={`step-card border-0 relative m-3.5 flex aspect-square w-[250px] flex-col items-center justify-start text-foreground ${!lastReachedStep && balance < workspaceSteps[0].stepPoint ? 'bg-primary' : ''}`}
            >
              {!lastReachedStep && balance < workspaceSteps[0].stepPoint && (
                <ProgramLogicStepLevelIndicator />
              )}

              <CardHeader className="flex justify-center items-center space-y-2">
                <TrophyIcon size={45} strokeWidth={1.5} />
                <p className="text-sm font-light">{t('program.step.name')} 0</p>
              </CardHeader>

              <CardContent className="pb-3 text-center">
                <p className="text-xl font-bold">{t('program.logic.first.step.label')}</p>
              </CardContent>
            </Card>

            {workspaceSteps?.map((step, i) => (
              <Card
                key={getWorkspaceStepKey(step, i)}
                className={`step-card border-0 relative m-3.5 flex aspect-square w-[250px] flex-col items-center justify-start text-foreground ${lastReachedStep?.name === step.name && lastReachedStep?.stepPoint >= step.stepPoint ? 'bg-primary' : ''}`}
              >
                {lastReachedStep?.name === step.name &&
                  lastReachedStep?.stepPoint >= step.stepPoint && (
                    <ProgramLogicStepLevelIndicator />
                  )}

                <CardHeader className="flex justify-center items-center space-y-2">
                  <TrophyIcon size={45} strokeWidth={1.5} />
                  <p className="text-sm font-light">
                    {t('program.step.name')} {i + 1}
                  </p>
                </CardHeader>

                <CardContent className="pb-3">
                  <p className="font-bold">{pointsTranslation(t, step.stepPoint, true)}</p>
                </CardContent>

                <CardFooter className="text-center">
                  <p className="text-sm font-light">{step.description}</p>
                </CardFooter>
              </Card>
            ))}

            {(isWorkspacePending || isWorkspaceError) &&
              Array.from({ length: 5 }).map((_, i) => (
                <Card
                  key={i}
                  className={
                    'step-card relative m-3.5 flex aspect-square w-[250px] flex-col items-center justify-start'
                  }
                >
                  <CardHeader className="flex justify-center items-center space-y-2">
                    <Skeleton className="h-14 aspect-square" />
                    <Skeleton className="w-full h-5" />
                  </CardHeader>

                  <CardContent className="pb-3 w-full">
                    <Skeleton className="mx-auto w-7/12 h-7" />
                  </CardContent>

                  <CardFooter className="flex-col w-full">
                    <Skeleton className="mb-2 w-full h-4" />
                    <Skeleton className="mb-2 w-full h-4" />
                    <Skeleton className="w-6/12 h-4" />
                  </CardFooter>
                </Card>
              ))}
          </div>
        </section>
      )}
    </PageContainer>
  );
};
